import React, { useEffect } from 'react';
import { Select, Input, Button, Form, Drawer, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import TestEmbedModal from '../test-embed/TestEmbedModal';
import { useCreateAiAvatarMutation } from '../../../components/generated/graphql';
import styles from './AI-avatars.module.css';

export interface AIAvatarConfig {
  avatarType: string;
  llmConfig: {
    provider: string;
    model: string;
  };
  ttsConfig: {
    provider: string;
    voiceId?: string;
  };
  botDefinition: {
    botName: string;
    personality: string;
    initialMessage: string;
  };
}

const baseURL2022Global = 'https://d23hwblhugcea5.cloudfront.net/Global/';

const ERROR_CREATE_AVATAR = 'Failed to create AI Avatar';

const avatars = [
  {
    url: `${baseURL2022Global}Avatares2025/AI/Avatar_2.glb`,
    preview: `${baseURL2022Global}Avatares2025/AI/Preview/Avatar_2.jpg`,
  },
  {
    url: `${baseURL2022Global}Avatares2025/AI/Avatar_7.glb`,
    preview: `${baseURL2022Global}Avatares2025/AI/Preview/Avatar_7.jpg`,
  },
  {
    url: `${baseURL2022Global}Avatares2025/AI/Avatar_8.glb`,
    preview: `${baseURL2022Global}Avatares2025/AI/Preview/Avatar_8.jpg`,
  },
  {
    url: `${baseURL2022Global}Avatares2025/AI/Avatar_4.glb`,
    preview: `${baseURL2022Global}Avatares2025/AI/Preview/Avatar_4.jpg`,
  },
];

const llmModels = [
  { id: 'llama-3.1-8b-instant', name: 'Llama 3.1 Instant', provider: 'groq' },
  {
    id: 'llama-3.3-70b-versatile',
    name: 'LLama 3.1 Versatile',
    provider: 'groq',
  },
  { id: 'openai/gpt-oss-120b', name: 'GPT oss', provider: 'groq' },
  {
    id: 'deepseek-r1-distill-llama-70b',
    name: 'Deepseek R1',
    provider: 'groq',
  },
  { id: 'gpt-4-turbo-preview', name: 'GPT-4 Turbo 128K', provider: 'openai' },
  { id: 'gpt-4', name: 'GPT-4 8K', provider: 'openai' },
  { id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo 16K', provider: 'openai' },
  { id: 'gpt-3.5-turbo-0125', name: 'GPT-3.5 Turbo', provider: 'openai' },
  { id: 'Islamic-RAG', name: 'Islamic RAG', provider: 'fanar' },
];

const elevenLabsVoices = [
  { id: 'CwhRBWXzGAHq8TQ4Fs17', name: 'Roger' },
  { id: 'EXAVITQu4vr4xnSDxMaL', name: 'Sarah' },
  { id: 'FGY2WhTYpPnrIDTdsKH5', name: 'Laura' },
  { id: 'IKne3meq5aSn9XLyUdCD', name: 'Charlie' },
  { id: 'JBFqnCBsd6RMkjVDRZzb', name: 'George' },
  { id: 'N2lVS1w4EtoT3dr4eOWO', name: 'Callum' },
  { id: 'SAz9YHcvj6GT2YYXdXww', name: 'River' },
  { id: 'TX3LPaxmHKxFdv7VOQHJ', name: 'Liam' },
];

enum PRESET_TYPES {
  HEALTH_COACH = 'health-coach',
  TECH_ADVISOR = 'tech-advisor',
  MATH_COMPANION = 'math-companion',
  KIDS_COACH = 'kids-coach',
  CUSTOM = 'custom',
}

const presets = [
  {
    id: PRESET_TYPES.HEALTH_COACH,
    label: 'Health Coach: "Coach Vida"',
    botName: 'Coach Vida',
    personality:
      "You are a professional yet approachable health coach named Coach Vida. You speak with clarity and positivity, helping users define and reach fitness, nutrition, and wellness goals. You always ask for relevant metrics like age, weight, activity level, and goals before giving advice. Explain things simply and avoid medical jargon. Focus on realistic, holistic lifestyle improvements. Always limit your answers to 30 words. Be motivating but not overwhelming. Break guidance into manageable steps. You're here to support, not lecture.",
    initialMessage:
      "Hi! What are your health goals? Weight, energy, fitness? Let's build your plan.",
  },
  {
    id: PRESET_TYPES.TECH_ADVISOR,
    label: 'Technology Advisor: "Ava the Avatar Pro"',
    botName: 'Ava the Avatar Pro',
    personality:
      'You are Ava, a friendly but professional tech advisor who specializes in helping users create, customize, and deploy their 3D avatars using the CREATE AI tool. You explain how to select a persona, assign a brain, choose a voice, and embed the result into any webpage. Use simple terms, no code unless asked. Always break tasks into steps. Offer help on customization, embedding, and troubleshooting. Be helpful, clear, and responsive. Limit every reply to 30 words. Avoid tech jargon unless the user requests advanced help.',
    initialMessage:
      "Want to build your own 3D avatar? I'll help you step by step.",
  },
  {
    id: PRESET_TYPES.MATH_COMPANION,
    label: 'Math Companion for Kids: "Matho"',
    botName: 'Matho',
    personality:
      "You are Matho, a playful and smart math companion for kids aged 5 to 12. You make learning math fun with games, jokes, and friendly encouragement. Use simple language and friendly tone. Always ask the child their age first to adapt the level. Never lecture—ask questions and give short, clear answers. Praise often. Don't go over 30 words per reply. You can explain concepts like addition, subtraction, multiplication, shapes, or logic games. Keep the vibe fun, positive, and full of curiosity.",
    initialMessage:
      "Hi there! How old are you? I've got cool math games just for you!",
  },
  {
    id: PRESET_TYPES.KIDS_COACH,
    label: 'Kids Passion Coach: "Spark"',
    botName: 'Spark',
    personality:
      "You are Spark, a cheerful and curious coach who helps kids discover their passions through gentle, thoughtful conversation. You ask about what they love doing, what makes them excited, and what they imagine doing in the future. You speak simply, warmly, and with lots of encouragement. Use age-appropriate questions and examples. You're never pushy—just curious. Every response is under 30 words and always includes a positive message. Guide them to explore topics like art, space, animals, tech, or sports in a natural way.",
    initialMessage:
      "Hey! What do you love doing for fun? Let's go on a discovery adventure!",
  },
  {
    id: PRESET_TYPES.CUSTOM,
    label: 'Create your own',
    botName: '',
    personality: '',
    initialMessage: '',
  },
];

const AIAvatars = ({ loadAsset, selectedModel, setIsOpen, isOpen }) => {
  const [form] = Form.useForm();
  const [isTestEmbedModalVisible, setIsTestEmbedModalVisible] =
    React.useState<boolean>(false);
  const [selectedAvatarIndex, setSelectedAvatarIndex] =
    React.useState<number>(-1);
  const [botId, setBotId] = React.useState<string>('');
  const [createAIAvatar, { loading, error }] = useCreateAiAvatarMutation();
  const [avatarType, setAvatarType] = React.useState<string>('assistant');
  const [selectedPreset, setSelectedPreset] = React.useState<string>(
    PRESET_TYPES.CUSTOM
  );

  useEffect(() => {
    const botConfig = selectedModel?.props.asset?.metadata?.botConfig;
    if (botConfig) {
      setBotId(botConfig.botId);

      form.setFieldsValue({
        avatarType: botConfig.avatarType || 'assistant',
        llmModel: botConfig.llmConfig.model,
        voiceId: botConfig.ttsConfig.voiceId || elevenLabsVoices[0].id,
        botName: botConfig.botDefinition.botName,
        personality: botConfig.botDefinition.personality,
        initialMessage: botConfig.botDefinition.initialMessage,
      });

      setAvatarType(botConfig.avatarType || 'assistant');
      setSelectedPreset(PRESET_TYPES.CUSTOM);
    }
  }, [selectedModel, form]);

  const isCustomPresetSelected = selectedPreset === PRESET_TYPES.CUSTOM;

  const handlePresetChange = (value: string) => {
    setSelectedPreset(value);

    const preset = presets.find((p) => p.id === value);
    if (preset) {
      form.setFieldsValue({
        botName: preset.botName,
        personality: preset.personality,
        initialMessage: preset.initialMessage,
      });
    }
  };

  const onFinish = async (values) => {
    const selectedAvatar = avatars[selectedAvatarIndex];
    const selectedModel = llmModels.find(
      (model) => model.id === values.llmModel
    );
    const llmProvider = selectedModel ? selectedModel.provider : 'groq';

    const payload = {
      llmProvider: llmProvider,
      llmModel: values.llmModel,
      ttsProvider: 'elevenlabs',
      botName: values.botName,
      botPersonality: values.personality,
      initialMessage: values.initialMessage,
      modelUrl: selectedAvatar.url,
      ttsVoiceId: values.voiceId,
    };

    try {
      const { data } = await createAIAvatar({
        variables: { data: payload },
      });

      if (data?.createAIAvatar?.status === true) {
        setBotId(data.createAIAvatar.id || '');

        loadAsset('AI Avatar', selectedAvatar.url, {
          avatarType: values.avatarType,
          llmConfig: {
            provider: llmProvider,
            model: values.llmModel,
          },
          ttsConfig: {
            provider: 'elevenlabs',
            voiceId: values.voiceId,
          },
          botDefinition: {
            botName: values.botName,
            personality: values.personality,
            initialMessage: values.initialMessage,
          },
          botId: data.createAIAvatar.id,
        });
      } else {
        console.error(ERROR_CREATE_AVATAR);
      }
    } catch (error) {
      console.error(`${ERROR_CREATE_AVATAR}: ${error}`);
    }
  };

  const handleAvatarTypeChange = (value: string) => {
    setAvatarType(value);
  };

  if (loading) {
    return <p>Creating AI Avatar...</p>;
  }

  if (error) {
    return <p>Error creating AI Avatar: {error.message}</p>;
  }

  const getLlmModelOptions = () => {
    return llmModels.map((model) => ({
      value: model.id,
      label: model.name,
      showContactUs: model.id !== 'llama3-70b-8192',
    }));
  };

  return (
    <Drawer
      open={isOpen}
      onClose={() => setIsOpen(false)}
      closeIcon={false}
      footer={false}
      width={'90%'}
      zIndex={1001}
    >
      <div className={styles.container}>
        <div className={styles.title}>AI Avatars</div>

        <Form
          form={form}
          layout='vertical'
          onFinish={onFinish}
          initialValues={{
            avatarType: 'assistant',
            llmModel: llmModels[1].id,
            voiceId: elevenLabsVoices[0].id,
            preset: PRESET_TYPES.CUSTOM,
          }}
        >
          <div className={styles.formSection}>
            <div className={styles.sectionTitle}>Select Avatar Type</div>
            <div className={styles.selectWrapper}>
              <Form.Item name='avatarType'>
                <Select
                  className={styles.avatarTypeSelect}
                  options={[
                    { value: 'assistant', label: 'Assistant' },
                    { value: 'npc', label: 'NPC Conversation' },
                    { value: 'embed', label: 'Embed' },
                  ]}
                  onChange={handleAvatarTypeChange}
                />
              </Form.Item>
            </div>

            <div className={styles.sectionTitle}>Select AI Avatar</div>
            <div className={styles.avatarGrid}>
              {avatars.map((avatar, index) => (
                <div
                  key={index}
                  className={`${styles.avatarCard} ${
                    selectedAvatarIndex === index ? styles.selected : ''
                  }`}
                  onClick={() => setSelectedAvatarIndex(index)}
                  data-cy='ai-avatar-card'
                >
                  <img src={avatar.preview} alt='Avatar preview' />
                </div>
              ))}
            </div>

            <div className={styles.providerSection}>
              <div>
                <div className={styles.sectionTitle}>
                  Brain{' '}
                  <Tooltip title='which LLM is used'>
                    <InfoCircleOutlined />
                  </Tooltip>
                </div>
                <Form.Item name='llmModel'>
                  <Select
                    style={{ width: '100%' }}
                    options={getLlmModelOptions()}
                    optionLabelProp='label'
                    optionRender={(option) => (
                      <div className={styles.modelOption}>
                        {option.data.label}
                        {option.data.showContactUs && (
                          <span className={styles.contactUsText}>
                            (contact us)
                          </span>
                        )}
                      </div>
                    )}
                  />
                </Form.Item>
              </div>

              <div>
                <div className={styles.sectionTitle}>
                  Voice{' '}
                  <Tooltip title='select the voice of your character'>
                    <InfoCircleOutlined />
                  </Tooltip>
                </div>
                <Form.Item name='voiceId'>
                  <Select
                    style={{ width: '100%' }}
                    options={elevenLabsVoices.map((voice) => ({
                      value: voice.id,
                      label: voice.name,
                    }))}
                  />
                </Form.Item>
              </div>
            </div>

            <div className={styles.sectionTitle}>Preset</div>
            <Form.Item name='preset' initialValue={PRESET_TYPES.CUSTOM}>
              <Select
                className={styles.presetSelect}
                options={presets.map((preset) => ({
                  value: preset.id,
                  label: preset.label,
                }))}
                onChange={handlePresetChange}
              />
            </Form.Item>

            <div className={styles.sectionTitle}>
              Bot Name{' '}
              <Tooltip title='this is the name of your person'>
                <InfoCircleOutlined />
              </Tooltip>
            </div>
            <Form.Item name='botName'>
              <Input
                className={styles.botNameInput}
                placeholder='Enter bot name...'
                disabled={!isCustomPresetSelected}
              />
            </Form.Item>

            <div className={styles.textAreaSection}>
              <div>
                <div className={styles.sectionTitle}>
                  Bot Personality{' '}
                  <Tooltip title='this define a person'>
                    <InfoCircleOutlined />
                  </Tooltip>
                </div>
                <Form.Item name='personality'>
                  <Input.TextArea
                    placeholder='Enter bot personality...'
                    rows={4}
                    disabled={!isCustomPresetSelected}
                  />
                </Form.Item>
              </div>

              <div>
                <div className={styles.sectionTitle}>Initial Message</div>
                <Form.Item name='initialMessage'>
                  <Input.TextArea
                    placeholder='Enter initial message...'
                    rows={4}
                    disabled={!isCustomPresetSelected}
                  />
                </Form.Item>
              </div>
            </div>

            <div className={styles.buttonGroup}>
              <Button
                type='primary'
                onClick={() => form.submit()}
                className={styles.createButton}
              >
                Deploy
              </Button>
              {botId && avatarType === 'embed' && (
                <Button
                  type='default'
                  onClick={() => setIsTestEmbedModalVisible(true)}
                  className={styles.testEmbedButton}
                >
                  Test Embed
                </Button>
              )}
            </div>
          </div>
        </Form>
      </div>

      <TestEmbedModal
        isVisible={isTestEmbedModalVisible}
        setIsVisible={setIsTestEmbedModalVisible}
        botId={botId}
      />
    </Drawer>
  );
};

export default AIAvatars;
